import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FolderOpen, CheckCircle, Search, Upload, Download, Shield, FileText, Image, Sparkles, ArrowRight } from "lucide-react";
import { Badge } from "../../ui/badge";

const documents = [
  { name: "Insurance_Card.jpg", category: "Insurance", size: "2.4 MB", type: "IMG", color: "bg-blue-100 text-blue-700" },
  { name: "Consent_Form.pdf", category: "Forms", size: "128 KB", type: "PDF", color: "bg-blue-100 text-blue-700" },
  { name: "Periapical_14.dcm", category: "X-Rays", size: "512 KB", type: "DCM", color: "bg-indigo-100 text-indigo-700" },
  { name: "Referral_Letter.pdf", category: "Referrals", size: "96 KB", type: "PDF", color: "bg-sky-100 text-sky-700" },
  { name: "Treatment_Plan.pdf", category: "Clinical", size: "256 KB", type: "PDF", color: "bg-blue-100 text-blue-700" },
];

const categories = [
  { name: "Insurance", count: 12, icon: Shield },
  { name: "X-Rays & Imaging", count: 28, icon: Image },
  { name: "Forms & Consent", count: 8, icon: FileText },
  { name: "Lab Reports", count: 15, icon: FolderOpen },
];

const PMSFileManagementSlide = () => {
  const [uploadingFile, setUploadingFile] = useState(null);
  const [uploadStep, setUploadStep] = useState(0);

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        setUploadingFile(null);
        setUploadStep(0);
        await new Promise((r) => setTimeout(r, 3000));
        setUploadingFile("New_Xray_29.dcm");
        setUploadStep(1);
        await new Promise((r) => setTimeout(r, 2000));
        setUploadStep(2);
        await new Promise((r) => setTimeout(r, 1500));
        setUploadStep(3);
        await new Promise((r) => setTimeout(r, 5000));
      }
    };
    cycle();
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <FolderOpen className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Intelligent{" "}
              <span className="text-blue-600">Document Management</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Centralized document storage with AI-powered auto-categorization,
              full-text search, and secure access controls. Every file automatically
              organized and linked to the right patient.
            </p>
          </motion.div>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              "AI Auto-Sort",
              "Drag & Drop",
              "Full-Text Search",
              "Version Control",
              "Patient Portal",
              "Retention Policy",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-5 h-5 text-blue-600" />
                <span className="text-3xl font-bold text-blue-600">AI</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Auto-Categorize
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">256-bit</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Encryption
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Document Manager Mockup */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          {/* Upload AI overlay */}
          <AnimatePresence>
            {uploadStep >= 1 && uploadStep < 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="absolute z-20 top-4 right-4 bg-slate-900/90 backdrop-blur-md rounded-xl p-4 text-white w-[260px] shadow-2xl border border-white/10"
              >
                <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                  <span className="text-xs font-bold text-blue-400 uppercase tracking-wider">
                    AI Processing
                  </span>
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                </div>
                <div className="space-y-3">
                  <div className={`flex items-center gap-3 transition-opacity duration-500 ${uploadStep >= 1 ? "opacity-100" : "opacity-30"}`}>
                    <div className="p-1.5 bg-blue-500/20 rounded-lg text-blue-300">
                      <Upload className="w-4 h-4" />
                    </div>
                    <div className="text-xs">
                      <div className="font-medium text-slate-200">File Uploaded</div>
                      <div className="text-slate-400">New_Xray_29.dcm (1.2 MB)</div>
                    </div>
                  </div>
                  <div className={`h-4 w-0.5 bg-slate-700 ml-3.5 transition-colors duration-500 ${uploadStep >= 2 ? "bg-blue-500" : ""}`} />
                  <div className={`flex items-center gap-3 transition-opacity duration-500 ${uploadStep >= 2 ? "opacity-100" : "opacity-30"}`}>
                    <div className="p-1.5 bg-blue-500/20 rounded-lg text-blue-300">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <div className="text-xs">
                      <div className="font-medium text-slate-200">Auto-Categorizing...</div>
                      <div className="text-slate-400">Detected: X-Rays & Imaging</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success badge */}
          <AnimatePresence>
            {uploadStep === 3 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="absolute z-20 top-4 right-4 bg-blue-600 text-white rounded-xl px-4 py-3 shadow-xl flex items-center gap-2"
              >
                <CheckCircle className="w-5 h-5" />
                <span className="text-sm font-bold">Filed to X-Rays</span>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <FolderOpen className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Patient Documents</h3>
                  <p className="text-xs text-slate-500">Sarah Johnson &bull; 63 files</p>
                </div>
              </div>
              <div className="relative w-48">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search files..."
                  className="w-full pl-9 pr-4 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs focus:outline-none"
                  readOnly
                />
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* Categories Sidebar */}
              <div className="w-44 border-r border-slate-100 p-3 space-y-1.5">
                <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider mb-2 px-2">
                  Categories
                </div>
                {categories.map((cat, i) => {
                  const Icon = cat.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + i * 0.1 }}
                      className={`flex items-center gap-2 px-2 py-2 rounded-lg text-xs cursor-pointer transition-all ${
                        i === 1
                          ? "bg-blue-50 text-blue-700 font-bold border border-blue-200"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="flex-1 truncate">{cat.name}</span>
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 bg-white">
                        {cat.count}
                      </Badge>
                    </motion.div>
                  );
                })}
              </div>

              {/* File List */}
              <div className="flex-1 p-4 space-y-2 overflow-y-auto">
                {documents.map((doc, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-[10px] font-bold ${doc.color}`}>
                        {doc.type}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-900">{doc.name}</p>
                        <p className="text-[10px] text-slate-500">
                          {doc.category} &bull; {doc.size}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button className="p-1.5 hover:bg-white rounded-lg transition-colors">
                        <Download className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                    </div>
                  </motion.div>
                ))}

                {/* New file animation */}
                <AnimatePresence>
                  {uploadStep === 3 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border-2 border-blue-300 shadow-md ring-2 ring-blue-100"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center text-[10px] font-bold bg-blue-600 text-white">
                          <Sparkles className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-blue-900">New_Xray_29.dcm</p>
                          <p className="text-[10px] text-blue-600">
                            X-Rays & Imaging &bull; 1.2 MB &bull; Just now
                          </p>
                        </div>
                      </div>
                      <Badge className="bg-blue-600 text-white border-0 text-[10px]">
                        New
                      </Badge>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Bottom Upload Bar */}
            <div className="h-14 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-6">
              <div className="flex items-center gap-2 text-xs text-blue-600 font-bold">
                <Upload className="w-4 h-4" />
                <span>Drag & drop files here to upload</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-600">AI Auto-Sort Enabled</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSFileManagementSlide;
